import { useState, useCallback, useMemo } from 'react';
import Barcode from 'react-barcode';
import { Printer, Loader2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { SimpleSelect } from '@/components/ui/simple-select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { resolveSiteDefault } from '@/lib/label-template-default';
import { usePrintContainerLabels } from '@/api/hooks/usePrinting';
import {
  useLabelTemplates,
  usePrintTemplateLabel,
} from '@/api/hooks/useLabelTemplates';
import { useWarehouses } from '@/api/hooks/useWarehouses';
import { useWarehouseStore } from '@/stores/warehouse-store';

interface QuickPrintButtonProps {
  className?: string;
}

export function QuickPrintButton({ className }: QuickPrintButtonProps) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [text, setText] = useState('');
  const [pickedWarehouseId, setPickedWarehouseId] = useState('');

  const selectedWarehouseId = useWarehouseStore((s) => s.selectedWarehouseId);
  const isAllMode = useWarehouseStore((s) => s.isAllMode);

  // When the user has an active warehouse in the header switcher, route the
  // print there directly. In "All Warehouses" mode (admin), prompt for a
  // target via an inline picker.
  const needsWarehousePicker = isAllMode || !selectedWarehouseId;
  const { data: warehousesData, isPending: warehousesLoading } = useWarehouses({
    limit: 100,
  });
  const warehouses = useMemo(
    () => warehousesData?.data ?? [],
    [warehousesData],
  );

  const resolvedWarehouseId = needsWarehousePicker
    ? pickedWarehouseId
    : (selectedWarehouseId ?? '');
  const resolvedWarehouseName = useMemo(() => {
    if (!resolvedWarehouseId) return '';
    return (
      warehouses.find((w) => w.id === resolvedWarehouseId)?.name ??
      'this warehouse'
    );
  }, [warehouses, resolvedWarehouseId]);

  const printLabelMutation = usePrintContainerLabels();
  const printTemplateMutation = usePrintTemplateLabel();

  // Quick Print is the SOLE consumer of the GENERIC type. Resolved with the
  // shared precedence rule (warehouse-scoped default beats global) rather
  // than relying on the server's response ordering.
  const { data: genericTemplates } = useLabelTemplates({
    warehouseId: resolvedWarehouseId,
    type: 'GENERIC',
    activeOnly: true,
  });
  const genericDefault = resolveSiteDefault(
    genericTemplates,
    'GENERIC',
    resolvedWarehouseId || null,
  );

  const handlePrint = useCallback(async () => {
    const trimmed = text.trim();
    if (!trimmed || !resolvedWarehouseId) return;

    try {
      if (genericDefault) {
        await printTemplateMutation.mutateAsync({
          warehouseId: resolvedWarehouseId,
          templateId: genericDefault.id,
          values: { 'custom.value': trimmed },
        });
        toast.success('Printed');
        setText('');
        setIsOpen(false);
        return;
      }

      // No template: today's path exactly — the text + a Code 128 barcode of
      // the same text.
      await printLabelMutation.mutateAsync({
        warehouseId: resolvedWarehouseId,
        items: [{ id: 'quick', number: trimmed }],
      });
      toast.success('Printed');
      setText('');
      setIsOpen(false);
    } catch (error: unknown) {
      const status = (error as { response?: { status?: number } })?.response
        ?.status;
      const serverMsg = (
        error as { response?: { data?: { message?: string } } }
      )?.response?.data?.message;
      if (status === 404) {
        toast.error(
          `No default printer set for ${resolvedWarehouseName}. Set one in Settings → Printers → Warehouse Defaults.`,
          {
            action: {
              label: 'Open settings',
              onClick: () => navigate('/settings/printers'),
            },
          },
        );
        return;
      }
      const msg =
        serverMsg ||
        (error instanceof Error ? error.message : 'Failed to send to printer');
      toast.error(msg);
    }
  }, [
    text,
    resolvedWarehouseId,
    resolvedWarehouseName,
    printLabelMutation,
    printTemplateMutation,
    genericDefault,
    navigate,
  ]);

  // Live barcode of exactly what would be printed, so a user can scan it
  // straight off the screen without sending anything to a printer.
  const barcodeValue = text.trim();

  const isPrinting =
    printLabelMutation.isPending || printTemplateMutation.isPending;
  const canPrint =
    text.trim().length > 0 && !!resolvedWarehouseId && !isPrinting;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className={cn('relative', className)}
          aria-label="Quick print"
        >
          <Printer className="h-5 w-5" />
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-80" sideOffset={8}>
        <div className="space-y-3">
          <h3 className="font-semibold text-sm">Quick print</h3>

          {needsWarehousePicker && (
            <div className="grid gap-1.5">
              <Label htmlFor="quick-print-warehouse" className="text-xs">
                Warehouse
              </Label>
              <SimpleSelect
                value={pickedWarehouseId}
                onValueChange={(v) => setPickedWarehouseId(v ?? '')}
                placeholder={
                  warehousesLoading ? 'Loading…' : 'Select a warehouse'
                }
                disabled={warehousesLoading}
                options={warehouses.map((w) => ({
                  value: w.id,
                  label: `${w.code} - ${w.name}`,
                }))}
              />
            </div>
          )}

          <div className="grid gap-1.5">
            <Label htmlFor="quick-print-text" className="text-xs">
              Text to print
            </Label>
            <Textarea
              id="quick-print-text"
              autoFocus
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="Type anything…"
              className="min-h-[80px]"
            />
          </div>

          {barcodeValue && (
            <div className="grid gap-1.5">
              <Label className="text-xs">Scan it yourself</Label>
              <div className="flex justify-center rounded-md border bg-white p-2">
                <Barcode
                  value={barcodeValue}
                  format="CODE128"
                  width={1.4}
                  height={50}
                  margin={0}
                  fontSize={12}
                />
              </div>
            </div>
          )}

          <Button onClick={handlePrint} disabled={!canPrint} className="w-full">
            {isPrinting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Printing…
              </>
            ) : (
              <>
                <Printer className="mr-2 h-4 w-4" />
                Print
              </>
            )}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
